/* eslint-disable no-fallthrough */
import { NAMESPACE_KEYS } from "constants/mutable";
import { Map, List, fromJS } from "immutable";
import diff from "immutablediff";
import patch from "immutablepatch";
import * as actions from "actions/mutate";
import { ALLOCATION_DIMENSIONS_NAMESPACE, DELIVERY_PURCHASE_ORDERS_NAMESPACE } from "constants/product-collection";

export const getNamespace = (collectionId: string, userId: string, key: NAMESPACE_KEYS) => {
    return `MUTABLE/${collectionId}/${userId}/${key}`;
};

export const getValueFromLocalStorage = (collectionId: string, userId: string, key: NAMESPACE_KEYS, defaultValue?) => {
    return window.localStorage.getItem(getNamespace(collectionId, userId, key)) || defaultValue;
};

export const setValueToLocalStorage = (collectionId: string, userId: string, key: NAMESPACE_KEYS, value) => {
    return window.localStorage.setItem(getNamespace(collectionId, userId, key), value);
};

export const arrayMove = (list: List<any>, index: number, destinationIndex: number): List<any> => {
    if (
        index < 0 ||
        index >= list.size ||
        destinationIndex < 0 ||
        destinationIndex > list.size ||
        index === destinationIndex ||
        index + 1 === destinationIndex ||
        (destinationIndex - index >= 0 && destinationIndex - index <= 1)
    ) {
        return list;
    }
    const value = list.get(index);
    if (destinationIndex === list.size) {
        list = list.push(value);
    } else {
        list = list.insert(destinationIndex, value);
    }
    list = list.delete(destinationIndex <= index ? index + 1 : index);
    return list;
};

const transformToLegacy = (payload) => {
    payload = {
        ...payload,
        ...payload.operations[0],
    };
    delete payload.operations;
    return payload;
};

const getMutableMixedChildPathByMutableType = (mutableType: actions.MutableType) => {
    // Structure of list and map inside mutable mixed:
    // - mutable list: list.values.{zero_index}
    // - mutable map: map.fields.{key}
    switch (mutableType) {
        case actions.MutableType.List:
            return ["list", "values"];
        case actions.MutableType.Map:
            return ["map", "fields"];
        default:
            return [];
    }
};

export const mutateSnapshot = (snapshot, payload) => {
    let { operations } = payload;

    // Upconvert if needed
    if (!operations && payload.operation) {
        operations = [
            {
                ...payload,
            },
        ];
        delete operations[0].mutableType;
        delete operations[0].objectId;
    }

    operations.forEach((_operation) => {
        const { operation, value, destination_index, index, deltas } = _operation;
        const indexOrKey = getKeyByMutableType(_operation, payload.mutableType);

        switch (operation) {
            case actions.OperationType.Set:
                if (deltas) {
                    let currentItem = snapshot.getIn(indexOrKey);

                    deltas.forEach((delta) => {
                        const op = delta.get("op");

                        // When Map operation is `replace` and item is undefined, backfill it.
                        // Undefined `index` means it's not a list.
                        if (!currentItem && op === "replace" && index === undefined) {
                            currentItem = Map();
                        }
                        currentItem = patch(currentItem, List([delta]));
                    });
                    payload.value = currentItem;
                    snapshot = snapshot.setIn(indexOrKey, currentItem);
                } else {
                    snapshot = snapshot.setIn(indexOrKey, value);
                }
                break;
            case actions.OperationType.Remove:
                snapshot = snapshot.removeIn(indexOrKey);
                break;
            case actions.OperationType.Insert:
                // `indexOrKey.length > 1` is the case for mutable mixed
                if (indexOrKey.length > 1) {
                    const pathsToMutationObject = indexOrKey.slice(0, indexOrKey.length - 1);
                    const lastKey = indexOrKey[indexOrKey.length - 1];
                    snapshot = snapshot.updateIn(pathsToMutationObject, (child) => {
                        if (!child) {
                            snapshot.setIn(indexOrKey, value);
                            return fromJS([value]);
                        } else {
                            return child.insert(lastKey === undefined ? snapshot.size : lastKey, value);
                        }
                    });
                } else {
                    snapshot = snapshot.insert(index === undefined ? snapshot.size : index, value);
                }
                break;
            case actions.OperationType.Move:
                if (indexOrKey.length > 1) {
                    snapshot = snapshot.updateIn(indexOrKey, (child) => arrayMove(child, index, destination_index));
                } else {
                    snapshot = arrayMove(snapshot, index, destination_index);
                }
                break;
            case actions.OperationType.Clear:
                snapshot = snapshot.removeIn(indexOrKey);
                break;
            default:
        }
    });
    return snapshot;
};

/*
 * getMutateHistory()
 *
 * This must compute a reversed operation for history purposes.
 * It must also add a rowId for the row being operated on.
 */
export const getMutateHistory = (snapshot, payload) => {
    const { operations, mutableType, objectId, HEAD } = payload;

    const up = {
        objectId,
        operations: [],
        mutableType,
    };

    const down = {
        objectId,
        operations: [],
        mutableType,
    };

    operations.forEach((operation) => {
        const keyOrIndex = getKeyByMutableType(operation, mutableType);
        const oldValues = snapshot.getIn(keyOrIndex);
        const rowId = oldValues && getRowIdByObjectType(oldValues, HEAD.object_type);

        switch (operation.operation) {
            case actions.OperationType.Set:
                up.operations.push({
                    ...operation,
                    deltas: diff(oldValues, operation.value),
                    rowId,
                });
                down.operations.unshift({
                    childKey: operation.childKey,
                    operation: oldValues ? actions.OperationType.Set : actions.OperationType.Remove,
                    value: oldValues,
                    deltas: diff(operation.value, oldValues),
                    [mutableType === actions.MutableType.Map ? "key" : "index"]:
                        mutableType === actions.MutableType.Map ? operation.key : operation.index,
                    rowId,
                });
                break;
            case actions.OperationType.Remove:
                up.operations.push({
                    ...operation,
                    rowId,
                });
                if (mutableType === actions.MutableType.Map) {
                    down.operations.unshift({
                        value: oldValues,
                        operation: oldValues ? actions.OperationType.Set : actions.OperationType.Remove,
                        key: operation.key,
                        childKey: operation.childKey,
                        rowId,
                    });
                } else {
                    down.operations.unshift({
                        operation: actions.OperationType.Insert,
                        value: oldValues,
                        index: operation.index,
                        childKey: operation.childKey,
                        rowId,
                    });
                }
                break;
            case actions.OperationType.Insert:
                up.operations.push({
                    ...operation,
                    rowId: getRowIdByObjectType(operation.value, HEAD.object_type),
                });
                down.operations.unshift({
                    index: operation.index === undefined ? snapshot.size : operation.index,
                    operation: actions.OperationType.Remove,
                    rowId: getRowIdByObjectType(operation.value, HEAD.object_type),
                });
                break;
            case actions.OperationType.Move:
                up.operations.push({
                    ...operation,
                    rowId,
                });
                down.operations.unshift({
                    destination_index: operation.index,
                    index: operation.destination_index,
                    operation: operation.operation,
                    rowId,
                });
                break;
            case actions.OperationType.Clear:
                if (oldValues && operation.childKey) {
                    up.operations.push({
                        ...operation,
                        rowId,
                    });

                    const downOperations = [];
                    oldValues.forEach((value, key) => {
                        downOperations.push({
                            childKey: operation.childKey,
                            operation: actions.OperationType.Set,
                            value: value,
                            [mutableType === actions.MutableType.Map ? "key" : "index"]: key,
                            rowId,
                        });
                    });

                    down.operations = downOperations.concat(down.operations);
                }
                break;
            default:
        }
    });
    return { up, down };
};

export const getKeyByMutableType = (operation: actions.IOperation, mutableType: actions.MutableType) => {
    const { key, index, childKey } = operation;
    const keyOrIndex = mutableType === actions.MutableType.Map ? key : index;

    // childKey will exist for mutable mixed operation
    if (childKey) {
        return ([childKey] as Array<string | number>)
            .concat(getMutableMixedChildPathByMutableType(mutableType))
            .concat([keyOrIndex])
            .filter((key1) => key1 !== undefined && key1 !== null);
    }

    return [keyOrIndex];
};

export const getRowIdByObjectType = (rowData: Map<string, any>, objectType: string) => {
    if (objectType === ALLOCATION_DIMENSIONS_NAMESPACE) {
        return rowData.getIn(["struct_value", "id"]);
    } else if (objectType === DELIVERY_PURCHASE_ORDERS_NAMESPACE) {
        return rowData.get("id");
    }
    // Default to assume this is Collection's items
    return rowData.getIn(["fields", "id", "string_value"]);
};

interface IOperationTransforms {
    leftTransform: any[];
    rightTransform: any[];
}

export const getOperationTransforms = (objectId, left: any[], right: any[]): IOperationTransforms => {
    if (left.length === 0 || right.length === 0) {
        return { leftTransform: right, rightTransform: left };
    }
    if (left.length === 1 && right.length === 1) {
        // here will merger two operations
        return upgradeOperation(objectId, left[0], right[0]);
    }
    // recursive
    if (left.length > 1) {
        const result1 = getOperationTransforms(objectId, left.slice(0, left.length - 1), right);
        const result2 = getOperationTransforms(objectId, left.slice(left.length - 1), result1.leftTransform);
        return {
            leftTransform: result2.leftTransform,
            rightTransform: [...result1.rightTransform, ...result2.rightTransform],
        };
    } else {
        // Here left.length === 1 and right.length > 1
        const result1 = getOperationTransforms(objectId, left, right.slice(0, right.length - 1));
        const result2 = getOperationTransforms(objectId, result1.rightTransform, right.slice(right.length - 1));
        return {
            leftTransform: [...result1.leftTransform, ...result2.leftTransform],
            rightTransform: result2.rightTransform,
        };
    }
};

const upgradeOperation = (objectId, left, right): IOperationTransforms => {
    switch (left.mutableType) {
        case "LIST":
            switch (right.mutableType) {
                case "LIST":
                    switch (left.operation) {
                        case "insert":
                            switch (right.operation) {
                                case "insert":
                                    return mergeInsertInsert(objectId, left, right);
                                case "move":
                                    return mergeInsertMove(objectId, left, right);
                                case "remove":
                                    return mergeInsertRemove(objectId, left, right);
                                case "set":
                                    return mergeInsertSet(objectId, left, right);
                                default:
                            }
                            break;
                        case "move":
                            switch (right.operation) {
                                case "insert":
                                    return swap(mergeInsertMove(objectId, right, left));
                                case "move":
                                    return mergeMoveMove(objectId, left, right);
                                case "remove":
                                    return swap(mergeRemoveMove(objectId, right, left));
                                case "set":
                                    return mergeMoveSet(objectId, left, right);
                                default:
                            }
                            break;
                        case "remove":
                            switch (right.operation) {
                                case "insert":
                                    return swap(mergeInsertRemove(objectId, right, left));
                                case "move":
                                    return mergeRemoveMove(objectId, left, right);
                                case "remove":
                                    return mergeRemoveRemove(objectId, left, right);
                                case "set":
                                    return mergeRemoveSet(objectId, left, right);
                                default:
                            }
                            break;
                        case "set":
                            switch (right.operation) {
                                case "insert":
                                    return swap(mergeInsertSet(objectId, right, left));
                                case "move":
                                    return swap(mergeMoveSet(objectId, right, left));
                                case "remove":
                                    return swap(mergeRemoveSet(objectId, right, left));
                                case "set":
                                    return mergeSetSet(objectId, left, right);
                                default:
                            }
                            break;
                        default:
                    }
                default:
            }
        default:
    }
    return { leftTransform: [], rightTransform: [] };
};

const swap = ({ leftTransform, rightTransform }) => ({ leftTransform: rightTransform, rightTransform: leftTransform });

const mergeInsertInsert = (objectId, left, right): IOperationTransforms => {
    const leftTransform = [];
    const rightTransform = [];
    if (left.index <= right.index) {
        // if lIndex == rIndex, right will insert to the position after left
        leftTransform.push(
            transformToLegacy(actions.listInsertOperation(objectId, right.value, right.index + 1).payload)
        );
        rightTransform.push(transformToLegacy(actions.listInsertOperation(objectId, left.value, left.index).payload));
    } else {
        leftTransform.push(transformToLegacy(actions.listInsertOperation(objectId, right.value, right.index).payload));
        rightTransform.push(
            transformToLegacy(actions.listInsertOperation(objectId, left.value, left.index + 1).payload)
        );
    }
    return {
        leftTransform,
        rightTransform,
    };
};

const mergeInsertMove = (objectId, left, right): IOperationTransforms => {
    const leftTransform = [];
    const rightTransform = [];
    const leftIndex = left.index;
    const rightIndex = right.index;
    const rightDestination = right.destination_index;

    if (rightIndex < rightDestination) {
        if (leftIndex <= rightIndex) {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex + 1, rightDestination + 1).payload)
            );
            rightTransform.push(
                transformToLegacy(actions.listInsertOperation(objectId, left.value, leftIndex).payload)
            );
        } else if (leftIndex < rightDestination) {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination + 1).payload)
            );
            rightTransform.push(
                transformToLegacy(actions.listInsertOperation(objectId, left.value, leftIndex - 1).payload)
            );
        } else {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
            );
            rightTransform.push(
                transformToLegacy(actions.listInsertOperation(objectId, left.value, leftIndex).payload)
            );
        }
    } else {
        if (leftIndex <= rightDestination) {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex + 1, rightDestination + 1).payload)
            );
            rightTransform.push(
                transformToLegacy(actions.listInsertOperation(objectId, left.value, leftIndex).payload)
            );
        } else if (leftIndex < rightIndex) {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex + 1, rightDestination).payload)
            );
            rightTransform.push(
                transformToLegacy(actions.listInsertOperation(objectId, left.value, leftIndex + 1).payload)
            );
        } else {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
            );
            rightTransform.push(
                transformToLegacy(actions.listInsertOperation(objectId, left.value, leftIndex).payload)
            );
        }
    }
    return {
        leftTransform,
        rightTransform,
    };
};

const mergeSetSet = (objectId, left, right): IOperationTransforms => {
    const leftTransform = [];
    const rightTransform = [];
    if (left.index === right.index) {
        // right wins if two index are the same
        leftTransform.push(
            transformToLegacy(actions.listSetOperation(objectId, left.index, right.value, right.deltas).payload)
        );
    } else {
        leftTransform.push(
            transformToLegacy(actions.listSetOperation(objectId, right.index, right.value, right.deltas).payload)
        );
        rightTransform.push(
            transformToLegacy(actions.listSetOperation(objectId, left.index, left.value, left.deltas).payload)
        );
    }
    return {
        leftTransform,
        rightTransform,
    };
};

const mergeInsertRemove = (objectId, left, right): IOperationTransforms => {
    const leftTransform = [];
    const rightTransform = [];
    if (left.index <= right.index) {
        leftTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, right.index + 1).payload));
        rightTransform.push(transformToLegacy(actions.listInsertOperation(objectId, left.value, left.index).payload));
    } else {
        leftTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, right.index).payload));
        rightTransform.push(
            transformToLegacy(actions.listInsertOperation(objectId, left.value, left.index - 1).payload)
        );
    }
    return {
        leftTransform,
        rightTransform,
    };
};

const mergeInsertSet = (objectId, left, right): IOperationTransforms => {
    const leftTransform = [];
    const rightTransform = [];
    if (left.index <= right.index) {
        leftTransform.push(
            transformToLegacy(actions.listSetOperation(objectId, right.index + 1, right.value, right.deltas).payload)
        );
        rightTransform.push(transformToLegacy(actions.listInsertOperation(objectId, left.value, left.index).payload));
    } else {
        leftTransform.push(
            transformToLegacy(actions.listSetOperation(objectId, right.index, right.value, right.deltas).payload)
        );
        rightTransform.push(transformToLegacy(actions.listInsertOperation(objectId, left.value, left.index).payload));
    }
    return {
        leftTransform,
        rightTransform,
    };
};

const mergeRemoveRemove = (objectId, left, right): IOperationTransforms => {
    const leftTransform = [];
    const rightTransform = [];
    if (left.index < right.index) {
        leftTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, right.index - 1).payload));
        rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, left.index).payload));
    } else if (left.index > right.index) {
        leftTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, right.index).payload));
        rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, left.index - 1).payload));
    }
    return {
        leftTransform,
        rightTransform,
    };
};

const mergeRemoveMove = (objectId, left, right): IOperationTransforms => {
    const leftTransform = [];
    const rightTransform = [];
    const leftIndex = left.index;
    const rightIndex = right.index;
    const rightDestination = right.destination_index;

    if (rightIndex < rightDestination) {
        if (leftIndex < rightIndex) {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex - 1, rightDestination - 1).payload)
            );
            rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, leftIndex).payload));
        } else if (leftIndex === rightIndex) {
            rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, rightDestination - 1).payload));
        } else if (leftIndex < rightDestination) {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination - 1).payload)
            );
            rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, leftIndex - 1).payload));
        } else {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
            );
            rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, leftIndex).payload));
        }
    } else {
        if (leftIndex < rightDestination) {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex - 1, rightDestination - 1).payload)
            );
            rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, leftIndex).payload));
        } else if (leftIndex < rightIndex) {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex - 1, rightDestination).payload)
            );
            rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, leftIndex + 1).payload));
        } else if (leftIndex === rightIndex) {
            rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, rightDestination).payload));
        } else {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
            );
            rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, leftIndex).payload));
        }
    }
    return {
        leftTransform,
        rightTransform,
    };
};

const mergeMoveSet = (objectId, left, right): IOperationTransforms => {
    const leftTransform = [];
    const rightTransform = [];
    const leftIndex = left.index;
    const rightIndex = right.index;
    const leftDestination = left.destination_index;

    if (leftIndex < leftDestination) {
        if (rightIndex < leftIndex) {
            leftTransform.push(
                transformToLegacy(actions.listSetOperation(objectId, rightIndex, right.value, right.deltas).payload)
            );
        } else if (leftIndex === rightIndex) {
            leftTransform.push(
                transformToLegacy(
                    actions.listSetOperation(objectId, leftDestination - 1, right.value, right.deltas).payload
                )
            );
        } else if (rightIndex < leftDestination) {
            leftTransform.push(
                transformToLegacy(actions.listSetOperation(objectId, rightIndex - 1, right.value, right.deltas).payload)
            );
        } else {
            leftTransform.push(
                transformToLegacy(actions.listSetOperation(objectId, rightIndex, right.value, right.deltas).payload)
            );
        }
    } else {
        if (rightIndex < leftDestination) {
            leftTransform.push(
                transformToLegacy(actions.listSetOperation(objectId, rightIndex, right.value, right.deltas).payload)
            );
        } else if (rightIndex < leftIndex) {
            leftTransform.push(
                transformToLegacy(actions.listSetOperation(objectId, rightIndex + 1, right.value, right.deltas).payload)
            );
        } else if (rightIndex === leftIndex) {
            leftTransform.push(
                transformToLegacy(
                    actions.listSetOperation(objectId, leftDestination, right.value, right.deltas).payload
                )
            );
        } else {
            leftTransform.push(
                transformToLegacy(actions.listSetOperation(objectId, rightIndex, right.value, right.deltas).payload)
            );
        }
    }
    rightTransform.push(transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload));
    return {
        leftTransform,
        rightTransform,
    };
};

const mergeRemoveSet = (objectId, left, right): IOperationTransforms => {
    const leftTransform = [];
    const rightTransform = [];
    if (left.index < right.index) {
        leftTransform.push(
            transformToLegacy(actions.listSetOperation(objectId, right.index - 1, right.value, right.deltas).payload)
        );
    } else if (left.index > right.index) {
        leftTransform.push(
            transformToLegacy(actions.listSetOperation(objectId, right.index, right.value, right.deltas).payload)
        );
    }
    rightTransform.push(transformToLegacy(actions.listRemoveOperation(objectId, left.index).payload));
    return {
        leftTransform,
        rightTransform,
    };
};

const mergeMoveMove = (objectId, left, right): IOperationTransforms => {
    let leftTransform = [];
    let rightTransform = [];
    const leftIndex = left.index;
    const leftDestination = left.destination_index;
    const rightIndex = right.index;
    const rightDestination = right.destination_index;

    if (leftIndex < leftDestination && rightIndex < rightDestination) {
        if (leftIndex < rightIndex) {
            if (leftDestination <= rightIndex) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload)
                );
            } else if (leftDestination <= rightDestination) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex - 1, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination - 1).payload)
                );
            } else {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex - 1, rightDestination - 1).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload)
                );
            }
        } else if (leftIndex === rightIndex) {
            if (leftDestination < rightDestination) {
                leftTransform.push(
                    transformToLegacy(
                        actions.listMoveOperation(objectId, leftDestination - 1, rightDestination).payload
                    )
                );
            } else if (leftDestination > rightDestination) {
                leftTransform.push(
                    transformToLegacy(
                        actions.listMoveOperation(objectId, leftDestination - 1, rightDestination - 1).payload
                    )
                );
            }
        } else if (leftIndex < rightDestination) {
            if (leftDestination <= rightDestination) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex - 1, leftDestination - 1).payload)
                );
            } else {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination - 1).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex - 1, leftDestination).payload)
                );
            }
        } else {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
            );
            rightTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload)
            );
        }
    } else if (leftIndex < leftDestination && rightIndex > rightDestination) {
        if (leftIndex < rightDestination) {
            if (leftDestination <= rightDestination) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload)
                );
            } else if (leftDestination <= rightIndex) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination - 1).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination + 1).payload)
                );
            } else {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex - 1, rightDestination - 1).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload)
                );
            }
        } else if (leftIndex === rightDestination) {
            if (leftDestination <= rightIndex) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex + 1, leftDestination + 1).payload)
                );
            } else {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex - 1, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex + 1, leftDestination).payload)
                );
            }
        } else if (leftIndex < rightIndex) {
            if (leftDestination <= rightIndex) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex + 1, leftDestination + 1).payload)
                );
            } else {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex - 1, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex + 1, leftDestination).payload)
                );
            }
        } else if (leftIndex === rightIndex) {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, leftDestination - 1, rightDestination).payload)
            );
        } else {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
            );
            rightTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload)
            );
        }
    } else if (leftIndex > leftDestination && rightIndex < rightDestination) {
        const transforms = swap(mergeMoveMove(objectId, right, left));
        leftTransform = transforms.leftTransform;
        rightTransform = transforms.rightTransform;
    } else {
        if (leftDestination < rightDestination) {
            if (leftIndex < rightDestination) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload)
                );
            } else if (leftIndex < rightIndex) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination + 1).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex + 1, leftDestination).payload)
                );
            } else if (leftIndex === rightIndex) {
                leftTransform.push(
                    transformToLegacy(
                        actions.listMoveOperation(objectId, leftDestination, rightDestination + 1).payload
                    )
                );
            } else {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex + 1, rightDestination + 1).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload)
                );
            }
        } else if (leftDestination === rightDestination) {
            if (leftIndex < rightIndex) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination + 1).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex + 1, leftDestination).payload)
                );
            } else if (leftIndex > rightIndex) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex + 1, rightDestination + 1).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload)
                );
            }
        } else if (leftDestination < rightIndex) {
            if (leftIndex < rightIndex) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex + 1, leftDestination + 1).payload)
                );
            } else if (leftIndex === rightIndex) {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftDestination, rightDestination).payload)
                );
            } else {
                leftTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, rightIndex + 1, rightDestination).payload)
                );
                rightTransform.push(
                    transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination + 1).payload)
                );
            }
        } else if (leftDestination === rightIndex) {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex + 1, rightDestination).payload)
            );
            rightTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination + 1).payload)
            );
        } else {
            leftTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, rightIndex, rightDestination).payload)
            );
            rightTransform.push(
                transformToLegacy(actions.listMoveOperation(objectId, leftIndex, leftDestination).payload)
            );
        }
    }
    return {
        leftTransform,
        rightTransform,
    };
};

const OPERATIONS_BATCH_SIZE = 25;

// Take all list mutation groups and break operations into batches.
// This is useful if row has big payload and sending all rows in single mutation group will hit maximum request size limit.
// Caveat 1: Only immediate SET operations are batched.
// Caveat 2: When there is immediate non SET operations, they should be grouped together, unbatched.
// Caveat 3: This is only for LIST type.
export const batchMutations = (
    objectId: string,
    mutations: actions.IOperationGroup | actions.IOperationGroup[],
    batchSize = OPERATIONS_BATCH_SIZE
) => {
    const mutationsList: actions.IOperationGroup[] = Array.isArray(mutations) ? mutations : [mutations];

    // Combine all operations from multiple groups into single group.
    // Since multiple groups can have different objectId, group them by objectId.
    const combinedMutationGroups = mutationsList.reduce((acc, mutations) => {
        // MutableMixed object type can contain both LIST and MAP.
        // The batch group key has to be composite keys of object id and object type.
        const key = `${mutations.objectId}_${mutations.mutableType}`;
        acc[key] = {
            ...mutations,
            operations: (acc[key]?.operations || []).concat(mutations.operations),
        };
        return acc;
    }, {});

    // Take combined operations above, break them into batched operations and group them by objectId.
    const batchedMutations = Object.keys(combinedMutationGroups).reduce((acc, combinedMutationsKey) => {
        const mutableType = combinedMutationGroups[combinedMutationsKey].mutableType;
        const operations = combinedMutationGroups[combinedMutationsKey].operations;
        let setOperationIndicesMap = {};
        let currentBatchOperations;
        let lastOperation;
        let operationCounter = 0;

        if (!acc[combinedMutationsKey] && operations.length > 0) {
            acc[combinedMutationsKey] = [];
        }

        const batchResult: any[] = acc[combinedMutationsKey];
        while (operations.length > 0) {
            if (operationCounter === 0 || operationCounter === batchSize) {
                batchResult.push({
                    ...combinedMutationGroups[combinedMutationsKey],
                    operations: [],
                });

                operationCounter = 0;
            }

            currentBatchOperations = batchResult[batchResult.length - 1].operations;
            lastOperation = currentBatchOperations[currentBatchOperations.length - 1];

            const operation = operations[0];

            if (mutableType === actions.MutableType.List) {
                // Special handling for SET operations: always keep operation to same-index/key together for data integrity.
                // Counting them as one since they will be sent to server as single SET operation.
                if (operation.operation === actions.OperationType.Set) {
                    const key = `${operation.childKey}${operation.index ?? operation.key}`;
                    if (setOperationIndicesMap[key] !== undefined) {
                        // Check if same index exists in other group, if yes, push this to that group.
                        // Move on to next iteration.
                        batchResult[setOperationIndicesMap[key]].operations.push(operations.shift());
                        continue;
                    } else if (lastOperation && lastOperation.operation !== operation.operation) {
                        // Check if previous operation is not empty and not SET operation.
                        // In that case, redo the loop and insert this operation to new batch.
                        operationCounter = 0;
                        // Clear indices map for next set of immediate SET operations
                        setOperationIndicesMap = {};
                        continue;
                    } else {
                        // Cache SET operation's target index. This will be used to group operations to the same index together.
                        setOperationIndicesMap[key] = batchResult.length - 1;
                    }
                } else {
                    if (lastOperation && lastOperation.operation === actions.OperationType.Set) {
                        // Check if previous operation is not empty and SET operation.
                        // In that case, redo the loop and insert this operation to new batch.
                        operationCounter = 0;
                        continue;
                    }
                }
            }

            // We've arrived here, so we're appending an operation.
            operationCounter++;
            currentBatchOperations.push(operations.shift());
        }

        return acc;
    }, {});

    // This is a workaround to support MutableMixed object type. This object can contain both LIST and MAP.
    // In such a case, the batch group key has to be composite keys of object id and object type.
    // Therefore, we have to lookup by object id with `indexOf`.
    const objectMutations = Object.keys(batchedMutations).reduce((acc, batchKey) => {
        if (batchKey.indexOf(`${objectId}_`) === 0) {
            acc = acc.concat(batchedMutations[batchKey]);
        }

        return acc;
    }, []);

    return objectMutations;
};

// Take list of operations and sort subsequent remove operations so higher index will be removed first.
export const sortRemoveOperations = (operations: actions.IOperation[]) => {
    let temporaryGroup = [];
    let sortedOperations = [];

    for (let i = 0; i < operations.length; i++) {
        const nextOperation = operations[i + 1];
        const operation = operations[i];
        if (operation.operation === actions.OperationType.Remove) {
            temporaryGroup.push(operation);

            // When there is no more operation, let it flush temporary group into sorted list.
            if (nextOperation) {
                continue;
            }
        }

        sortedOperations = sortedOperations.concat(
            temporaryGroup.sort((operation1, operation2) => {
                return operation2.index - operation1.index;
            })
        );

        temporaryGroup = [];

        if (operation.operation !== actions.OperationType.Remove) {
            sortedOperations.push(operation);
        }
    }

    return sortedOperations;
};

// HACK. Adding childKey which is required for `mutateSnapshot` to be able to correctly mutate mutable_mixed object
// TODO: Update `getOperationTransform` to support mutable mixed OT. https://nuorder-inc.atlassian.net/browse/NU-23134
// Currently, it's not supported, therefore, `getOperationTransforms` is returning `LIST` transform operations regardless of origin mutable_mixed operations
export const coerceOperationTransform = (operation, mutation) => {
    if (mutation.objectId.match(/^allocation_dimension/i) && mutation.mutableType === actions.MutableType.List) {
        operation.childKey = "dimension_values";
    }

    return operation;
};
